/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Download, X, Tags, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useRegisterPageShortcuts } from "@/hooks/usePageShortcuts";
import { decideExcerptKeyAction } from "@/lib/excerptListKeys";
import { duplicateExcerpt, exportExcerptsCsv } from "@/lib/excerpts";
import { ExcerptsBulkBar } from "@/components/excerpts/ExcerptsBulkBar";
import { type ExcerptRow } from "@/components/excerpts/ExcerptCard";
import { ExcerptsFilterBar } from "@/components/excerpts/ExcerptsFilterBar";
import { WorkspaceChips } from "@/components/library/WorkspaceChips";
import { AppSeo } from "@/components/AppSeo";
import { TagsManagerSheet } from "@/components/excerpts/TagsManagerSheet";
import { ExcerptsSourceContextCard } from "@/components/excerpts/ExcerptsSourceContextCard";
import { ExcerptsListGrid } from "@/components/excerpts/ExcerptsListGrid";
import { useExcerptsBulkActions } from "@/components/excerpts/useExcerptsBulkActions";
import { useExcerptsData } from "@/components/excerpts/useExcerptsData";

const Excerpts = () => {
  const navigate = useNavigate();
  const d = useExcerptsData();
  const {
    active, workspaces, libraryMode,
    searchParams, setSearchParams,
    tagFilters, topicFilters, sourceTypeFilters, searchQuery,
    libraryItemFilter, returnTo, returnHref, returnLabel,
    excerptViewReturnTo, scrollKey,
    items, loading, librarySource, tags, topics, setTags,
    selectedWorkspaceIds, setSelectedWorkspaceIds,
    hasMore, loadingMore, loadMore,
    load,
  } = d;


  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const scopeWorkspaceIds = useMemo(() => {
    if (libraryMode === "shared" && workspaces.length > 1) return workspaces.map((w) => w.id);
    return active ? [active.id] : [];
  }, [libraryMode, workspaces, active]);

  useEffect(() => {
    let cancelled = false;
    if (scopeWorkspaceIds.length === 0) {
      setTotalCount(null);
      return;
    }
    supabase
      .from("excerpts")
      .select("id", { count: "exact", head: true })
      .in("workspace_id", scopeWorkspaceIds)
      .then(({ count, error }) => {
        if (cancelled) return;
        if (error) {
          setTotalCount(null);
          return;
        }
        setTotalCount(count ?? 0);
      });
    return () => {
      cancelled = true;
    };
    // Re-run when scope changes or list size changes (create/delete).
  }, [scopeWorkspaceIds, items.length]);

  useEffect(() => {
    setSelectedWorkspaceIds([]);
    setSelected(new Set());
    setSelectMode(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  useRegisterPageShortcuts("excerpts", [
    {
      heading: "Excerpts list",
      items: [
        { keys: ["J", "↓"], label: "Move focus to next excerpt" },
        { keys: ["K", "↑"], label: "Move focus to previous excerpt" },
        { keys: ["Enter"], label: "Open focused excerpt" },
        { keys: ["D"], label: "Duplicate focused excerpt" },
      ],
    },
  ]);

  const onDuplicate = async (e: React.MouseEvent, it: ExcerptRow) => {
    e.preventDefault();
    e.stopPropagation();
    if (!active) return;
    try {
      await duplicateExcerpt(it.id, active.id);
      toast.success("Duplicated");
      load();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to duplicate");
    }
  };

  useEffect(() => {
    if (loading || items.length === 0 || selectMode) return;
    const onKey = (e: KeyboardEvent) => {
      const cards = Array.from(
        document.querySelectorAll<HTMLAnchorElement>("[data-excerpt-card]")
      );
      const action = decideExcerptKeyAction({
        key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, altKey: e.altKey,
        activeElement: document.activeElement, cards,
      });
      if (action.type === "noop") return;
      if (action.type === "focus") {
        const el = cards[action.index];
        if (!el) return;
        e.preventDefault();
        el.focus();
        el.scrollIntoView({ block: "nearest" });
        return;
      }
      if (action.type === "duplicate") {
        const it = items.find((x) => x.id === action.id);
        if (!it) return;
        e.preventDefault();
        onDuplicate({ preventDefault() {}, stopPropagation() {} } as any, it);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items, selectMode]);

  const onChipClick = (kind: "tag" | "topic", name: string) => {
    const next = new URLSearchParams(searchParams);
    const current = (kind === "tag" ? tagFilters : topicFilters);
    const lowered = current.map((c) => c.toLowerCase());
    const trimmed = name.trim();
    if (!trimmed) return;
    let nextValues: string[];
    if (lowered.includes(trimmed.toLowerCase())) {
      nextValues = current.filter((c) => c.toLowerCase() !== trimmed.toLowerCase());
    } else {
      nextValues = [...current, trimmed];
    }
    if (nextValues.length === 0) next.delete(kind);
    else next.set(kind, nextValues.join(","));
    setSearchParams(next);
  };

  const clearFilter = () => setSearchParams({});

  const sendToImageStudio = (e: React.MouseEvent, it: ExcerptRow) => {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem(
      "excerptus.imageStudioPrefill",
      JSON.stringify({
        bodyHtml: it.body_html,
        author: it.library_items?.author_text ?? "",
        publication: it.library_items?.display_title ?? it.library_items?.title ?? "",
      })
    );
    navigate("/image-studio");
  };

  const onExport = async () => {
    if (!active) return;
    setExporting(true);
    try {
      const n = await exportExcerptsCsv(active.id);
      toast.success(`Exported ${n} excerpt${n === 1 ? "" : "s"}`);
    } catch (err: any) {
      toast.error(err.message ?? "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = useMemo(
    () => items.length > 0 && selected.size === items.length,
    [items, selected],
  );

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const {
    bulkBusy, newTagName, setNewTagName, newTopicName, setNewTopicName,
    exitSelectMode, bulkMoveToWorkspace, bulkDelete, bulkAddTag, bulkAddTopic,
    handleCreateAndTag, handleCreateAndAddTopic,
  } = useExcerptsBulkActions({
    activeWorkspaceId: active?.id ?? null,
    workspaces, selected, setSelected, setSelectMode,
    tags, setTags, reload: load,
  });

  return (
    <>
      <AppSeo title="My Excerpts" />
      <div className="space-y-6">
        <header className="md:static sticky top-0 z-30 -mx-4 flex items-end justify-between gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:-mx-6 sm:px-6 md:mx-0 md:border-0 md:bg-transparent md:px-0 md:py-0 md:backdrop-blur-0">
          <div className="min-w-0">
            <h1 className="font-serif text-2xl font-semibold sm:text-3xl">My Excerpts</h1>
            <p className="hidden text-muted-foreground sm:block">Your collected excerpts.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {items.length > 0 && (
              <Button
                variant="outline" size="sm" className="sm:h-10 sm:px-4"
                onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                aria-label={selectMode ? "Cancel selection" : "Select excerpts"}
              >
                {selectMode && <X className="h-4 w-4" />}
                <span className={selectMode ? "hidden sm:inline" : ""}>{selectMode ? "Cancel" : "Select"}</span>
              </Button>
            )}
            <Button
              variant="outline" size="sm" className="sm:h-10 sm:px-4"
              onClick={onExport} disabled={exporting || !active} aria-label="Export CSV"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">{exporting ? "Exporting…" : "Export CSV"}</span>
            </Button>
            <TagsManagerSheet
              trigger={
                <Button variant="outline" size="sm" className="sm:h-10 sm:px-4" aria-label="Manage tags">
                  <Tags className="h-4 w-4" />
                  <span className="hidden sm:inline">Manage tags</span>
                </Button>
              }
            />
            <Link to="/new-excerpt">
              <Button size="sm" className="sm:h-10 sm:px-4" aria-label="New excerpt">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New excerpt</span>
              </Button>
            </Link>
          </div>
        </header>

        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="outline" className="gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium">
            <BookOpen className="h-3.5 w-3.5 text-primary" />
            <span className="text-muted-foreground">Excerpts</span>
            <span className="text-foreground">{totalCount ?? "—"}</span>
          </Badge>
        </div>


        <ExcerptsFilterBar
          tags={tags}
          topics={topics}
          tagFilters={tagFilters}
          topicFilters={topicFilters}
          sourceTypeFilters={sourceTypeFilters}
          searchQuery={searchQuery}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />

        {libraryMode === "shared" && workspaces.length > 1 ? (
          <WorkspaceChips
            workspaces={workspaces}
            selectedIds={selectedWorkspaceIds}
            onToggle={(id) =>
              setSelectedWorkspaceIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
              )
            }
            onClear={() => setSelectedWorkspaceIds([])}
          />
        ) : null}

        <ExcerptsSourceContextCard
          librarySource={librarySource}
          returnHref={returnHref}
          returnLabel={returnLabel}
          returnTo={returnTo}
          excerptViewReturnTo={excerptViewReturnTo}
          onClearLibraryItem={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("libraryItem");
            setSearchParams(next);
          }}
        />

        {selectMode && (
          <ExcerptsBulkBar
            selectedCount={selected.size}
            allSelected={allSelected}
            bulkBusy={bulkBusy}
            tags={tags}
            topics={topics}
            newTagName={newTagName}
            newTopicName={newTopicName}
            onToggleSelectAll={toggleSelectAll}
            onNewTagNameChange={setNewTagName}
            onNewTopicNameChange={setNewTopicName}
            onCreateAndTag={handleCreateAndTag}
            onCreateAndAddTopic={handleCreateAndAddTopic}
            onAddTag={bulkAddTag}
            onAddTopic={bulkAddTopic}
            onDelete={bulkDelete}
            hideTopics={active?.codex_enabled === false}
            topicsReadOnly={active?.codex_enabled !== false}
            workspaces={libraryMode === "shared" && workspaces.length > 1 ? workspaces : undefined}
            onMoveToWorkspace={libraryMode === "shared" && workspaces.length > 1 ? bulkMoveToWorkspace : undefined}
          />
        )}

        <ExcerptsListGrid
          loading={loading}
          items={items}
          searchQuery={searchQuery}
          searchParams={searchParams}
          hasFilters={
            tagFilters.length > 0 ||
            topicFilters.length > 0 ||
            !!searchQuery ||
            !!libraryItemFilter ||
            sourceTypeFilters.length > 0
          }
          selectMode={selectMode}
          selected={selected}
          onClearFilter={clearFilter}
          onToggleSelect={toggleSelect}
          onDuplicate={onDuplicate}
          onSendToImageStudio={sendToImageStudio}
          onChipClick={onChipClick}
          scrollKey={scrollKey}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
        />

      </div>
    </>
  );
};

export default Excerpts;
